import React from "react";
import { UseSetAsset } from "../../../api/calls/Queries";
import { Asset } from "../../../Types";
import { QueryObserverResult } from "@tanstack/react-query";
import ProgressBar from "../GuiElements/ProgressBar";
import reduceImage from "../../common/ImageReducer";
import AudioPreview, { formatBytes } from "./AudioPreview";
import { getLanguageFile } from "../../common/GetLanguageFile";
import { useAssetListStore } from "../../../api/Zustand";

const UploadPage = (props: {
  refetch: () => Promise<QueryObserverResult<Asset[] | undefined>>;
  setPage: React.Dispatch<React.SetStateAction<1 | 2 | 3>>;
  setSelectedAsset: React.Dispatch<
    React.SetStateAction<number | string | undefined>
  >;
  assetType: number | undefined;
  assets: Asset[];
}) => {
  const { refetch, setPage, setSelectedAsset, assetType, assets } = props;

  const [file, setFile] = React.useState<File | null>();
  const [previewBlob, setPreviewBlob] = React.useState<string | undefined>();
  const [fileDetails, setFileDetails] = React.useState<HTMLImageElement>();
  const [audioDetails, setAudioDetails] = React.useState<HTMLAudioElement>();

  const [progress, setProgress] = React.useState<number>();

  const { setLoading } = useAssetListStore();

  const {
    mutateAsync: uploadFile,
    isPending,
    isError,
    error,
  } = UseSetAsset(assetType!, setProgress);

  React.useEffect(() => {
    if (!file) {
      setFileDetails(undefined);
      setAudioDetails(undefined);

      return;
    }
    const objectUrl = URL.createObjectURL(file);

    if (assetType === 1 || assetType === 2) {
      let image = new Image();
      image.src = objectUrl;

      image.onload = () => {
        setPreviewBlob(image.src);
        setFileDetails(image);
      };

      return () => URL.revokeObjectURL(objectUrl);
    } else {
      const audio = new Audio();
      audio.preload = "metadata";
      audio.src = objectUrl;
      audio.onloadeddata = () => {
        setAudioDetails(audio);
      };
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [file, assetType]);

  const uploadAudio = React.useCallback(() => {
    uploadFile(
      {
        file: file as File,
        assetFileType: "F",
        assetId: 0,
      },
      {
        onSettled: (re) => {
          refetch();
          setPage(1);
          setLoading(false);
          setSelectedAsset(re.assetId);
          return;
        },
      }
    );
  }, [file, uploadFile, refetch, setPage, setSelectedAsset, setLoading]);

  const lowResUrl = React.useRef<string>();

  const generatePanoThumbnail = React.useCallback(async function (ev: File) {
    const resultCanvasThumb: HTMLCanvasElement = await reduceImage(
      ev,
      256,
      128
    );

    return new Promise((resolve) => {
      resultCanvasThumb.toBlob((blob) => {
        const file = new File([blob!], ev.name, { type: ev.type });
        resolve(file);
      });
    });
  }, []);

  const generatePanoLowRes = React.useCallback(
    async function (ev: File) {
      URL.revokeObjectURL(lowResUrl.current!);

      const resultCanvasLowRes = await reduceImage(ev, 724, 362);

      return new Promise((resolve) => {
        resultCanvasLowRes.toBlob((blob) => {
          lowResUrl.current = URL.createObjectURL(blob!);

          const file = new File([blob!], ev.name, { type: ev.type });

          resolve(file);
        });
      });
    },
    [lowResUrl]
  );

  const generateImageThumbnail = React.useCallback(
    async (ev: File) => {
      URL.revokeObjectURL(lowResUrl.current!);

      const resultCanvasThumb: HTMLCanvasElement = await reduceImage(
        ev,
        256,
        128
      );

      return new Promise((resolve) => {
        resultCanvasThumb.toBlob((blob) => {
          lowResUrl.current = URL.createObjectURL(blob!);

          const file = new File([blob!], ev.name, { type: ev.type });

          resolve(file);
        });
      });
    },
    [lowResUrl]
  );

  const annulla = () => {
    setFile(null);
    setPreviewBlob(undefined);
  };

  const [isUploadingMain, setIsUploadingMain] = React.useState(false);
  const [isGeneratingThumb, setIsGeneratingThumb] = React.useState(false);
  const [isUploadingThumb, setIsUploadingThumb] = React.useState(false);
  const [isGeneratingLo, setIsGeneratingLo] = React.useState(false);
  const [isUploadingLo, setIsUploadingLo] = React.useState(false);
  const [isLoadingGeneric, setIsLoadingGeneric] = React.useState(false);

  const onUploadComplete = React.useCallback(
    async (res: string) => {
      refetch().then(function () {
        setPage(1);
        setSelectedAsset(res);
        setFile(undefined);
      });
    },
    [refetch, setPage, setSelectedAsset, setFile]
  );
  const uploadImageImproved = React.useCallback(() => {
    setIsLoadingGeneric(true);

    try {
      setIsUploadingMain(true);

      uploadFile(
        { file: file as File, assetFileType: "F", assetId: 0 },
        {
          onSuccess: async (firtsUploadResp) => {
            setIsUploadingMain(false);

            setIsGeneratingThumb(true);

            // @ts-expect-error
            const lowResFile: File = await generateImageThumbnail(file!);
            setIsGeneratingThumb(false);
            setIsUploadingThumb(true);

            uploadFile(
              {
                file: lowResFile,
                assetFileType: "L",
                assetId: firtsUploadResp.assetId,
              },
              {
                onSettled: async () => {
                  setIsUploadingThumb(false);
                  setIsLoadingGeneric(false);
                  onUploadComplete(firtsUploadResp.assetId);
                },
              }
            );
          },
        }
      );
    } catch (err) {
      setIsLoadingGeneric(false);
      console.debug(err);
    }
  }, [file, generateImageThumbnail, onUploadComplete, uploadFile]);

  const uploadPanoImproved = React.useCallback(async () => {
    setIsLoadingGeneric(true);

    setIsUploadingMain(true);

    try {
      uploadFile(
        { file: file as File, assetFileType: "F", assetId: 0 },
        {
          onSuccess: async (firtsUploadResp) => {
            setIsUploadingMain(false);

            setIsGeneratingLo(true);

            // @ts-expect-error
            const lowResFile: File = await generatePanoLowRes(file!);

            setIsGeneratingLo(false);
            setIsUploadingLo(true);
            uploadFile(
              {
                file: lowResFile,
                assetFileType: "L",
                assetId: firtsUploadResp.assetId,
              },
              {
                onSettled: async () => {
                  setIsUploadingLo(false);

                  setIsGeneratingThumb(true);

                  // @ts-expect-error
                  const thumb: File = await generatePanoThumbnail(file);
                  setIsGeneratingThumb(false);
                  setIsUploadingThumb(true);

                  uploadFile(
                    {
                      file: thumb,
                      assetFileType: "T",
                      assetId: firtsUploadResp.assetId,
                    },
                    {
                      onSettled: () => {
                        setIsUploadingThumb(false);
                        setIsLoadingGeneric(false);
                        onUploadComplete(firtsUploadResp.assetId);
                      },
                    }
                  );
                },
              }
            );
          },
        }
      );
    } catch (error) {
      setIsLoadingGeneric(false);
      console.debug(error);
    }
  }, [
    file,
    generatePanoLowRes,
    uploadFile,
    onUploadComplete,
    generatePanoThumbnail,
  ]);

  const progressBarStatus = React.useMemo(() => {
    if (isUploadingMain) return getLanguageFile("overlapping_panel.up_main");
    else if (isGeneratingLo) return getLanguageFile("overlapping_panel.gen_lo");
    else if (isUploadingLo) return getLanguageFile("overlapping_panel.up_lo");
    else if (isGeneratingThumb)
      return getLanguageFile("overlapping_panel.gen_thumb");
    else if (isUploadingThumb)
      return getLanguageFile("overlapping_panel.up_thumb");
  }, [
    isUploadingLo,
    isUploadingMain,
    isGeneratingLo,
    isGeneratingThumb,
    isUploadingThumb,
  ]);
  const inLoadingState = (isPending || isLoadingGeneric) && !isError;

  React.useEffect(() => {
    setLoading(inLoadingState);
    // setAssetList((currVal) => {
    //   return { ...currVal, isLoading: inLoadingState };
    // });
  }, [inLoadingState, setLoading]);

  return (
    <div className="upload-file-page">
      {file && fileDetails ? (
        <>
          <div
            className="uploaded-thumbnail"
            style={{
              backgroundImage: `url(${previewBlob})`,
            }}
          >
            {progress && (
              <ProgressBar percentage={progress} status={progressBarStatus} />
            )}
          </div>

          <p>
            {file.name}, {formatBytes(file.size)}
          </p>
          {assets.find((one) => one.assetDescription === file.name) && (
            <>
              <br />
              <small style={{ color: "#fb3640" }}>
                ⓘ {getLanguageFile("overlapping_panel.name_duplicate")}
              </small>
              <br />
            </>
          )}
          <div style={{ display: "flex" }}>
            <button
              id="file-upload"
              onClick={() => {
                assetType === 1 ? uploadPanoImproved() : uploadImageImproved();
              }}
              disabled={inLoadingState || isLoadingGeneric}
            >
              {inLoadingState
                ? getLanguageFile("generics.uploading")
                : getLanguageFile("generics.upload")}
            </button>
            <button
              id="file-upload"
              onClick={() => annulla()}
              disabled={inLoadingState && !isError}
            >
              {getLanguageFile("generics.cancel")}
            </button>
          </div>
          <small className="file-size">
            {file.size / 1024000 > 10
              ? getLanguageFile("overlapping_panel.big_file_size")
              : null}
            <br />
            {assetType === 1 &&
            fileDetails?.naturalWidth / fileDetails?.naturalHeight !== 2 ? (
              <b>{getLanguageFile("overlapping_panel.wrong_aspect_ratio")}</b>
            ) : null}
          </small>
          {isError && (
            <div>
              <code>{error?.code}</code> {error?.message}
            </div>
          )}
        </>
      ) : file && audioDetails ? (
        <>
          <AudioPreview
            file={file}
            audioState={audioDetails}
            upPercentage={progress}
          />
          {assets.find((one) => one.assetDescription === file.name) && (
            <>
              <br />
              <small style={{ color: "#fb3640" }}>
                ⓘ {getLanguageFile("overlapping_panel.name_duplicate")}
              </small>
              <br />
            </>
          )}

          <small>
            {file.size / 1024000 > 10
              ? getLanguageFile("overlapping_panel.big_file_size")
              : null}
          </small>

          <div style={{ display: "flex" }}>
            <button
              id="file-upload"
              onClick={() => annulla()}
              disabled={inLoadingState}
            >
              {getLanguageFile("generics.cancel")}
            </button>
            <button
              id="file-upload"
              onClick={() => {
                uploadAudio();
              }}
              disabled={inLoadingState || !audioDetails}
            >
              {inLoadingState
                ? getLanguageFile("generics.uploading")
                : getLanguageFile("generics.upload")}
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="asset-list-upload">
            {getLanguageFile("overlapping_panel.select_file")}{" "}
            <input
              type="file"
              onChange={async (e) => {
                setPreviewBlob(undefined);

                setFile(e.target.files![0]);
              }}
              accept={assetType === 3 ? ".wav, .mp3" : ".jpg, .jpeg, .png"}
            />
          </label>
          <small className="file-size">
            {assetType === 1 ? (
              getLanguageFile("overlapping_panel.pano_reqs")
            ) : assetType === 2 ? (
              getLanguageFile("overlapping_panel.png_jpg")
            ) : (
              <small>{getLanguageFile("overlapping_panel.mp3_wav")} </small>
            )}
          </small>
        </>
      )}
    </div>
  );
};

export default UploadPage;
